/**
 * Payment — Stripe Elements card form.
 *
 * Stripe Elements renders inside its own iframe nested inside ours, so
 * card data never touches our DOM. PCI scope stays at Stripe. We hold
 * only the client_secret (a pointer, not a credential) and the
 * registration_id we created in ConfirmPage.
 *
 * The publishable key is exposed via Vite env var. In production it's
 * pinned per-environment; in dev a public test key is fine.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';

import type { EmbedClient, CreatePaymentIntentResponse } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { PaymentForm } from '@/components/PaymentForm';
import { themedStripeAppearance } from '@/styles/stripeAppearance';
import './PaymentPage.css';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

interface Props {
  client: EmbedClient;
}

export function PaymentPage({ client }: Props) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { selectedTeam, registration, setRegistration, addonQuantities } = useFlow();

  const [intent, setIntent] = useState<CreatePaymentIntentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stripe = useMemo<Promise<Stripe | null> | null>(() => {
    if (!STRIPE_PUBLISHABLE_KEY) return null;
    return loadStripe(STRIPE_PUBLISHABLE_KEY);
  }, []);

  useEffect(() => {
    if (!selectedTeam || !registration) {
      navigate(`/events/${eventId}/teams`, { replace: true });
      return;
    }
    // Fold any charity add-ons chosen on Confirm into this single charge.
    const addonItems = Object.entries(addonQuantities)
      .filter(([, qty]) => qty > 0)
      .map(([product_id, quantity]) => ({ product_id, quantity }));
    client
      .createPaymentIntent(selectedTeam.id, registration.id, addonItems)
      .then(setIntent)
      .catch((err) => {
        // 409: an earlier payment for this checkout went through or is
        // still processing; the server says which.
        const detail = err?.response?.data?.detail;
        setError(
          err?.response?.status === 409 && typeof detail === 'string'
            ? detail
            : 'Could not initialize payment. Please try again.',
        );
      });
  }, [client, eventId, selectedTeam, registration, addonQuantities, navigate]);

  if (!selectedTeam || !registration) return null;

  if (!STRIPE_PUBLISHABLE_KEY) {
    // Dev mode without Stripe configured. Skip straight to confirm so
    // the rest of the flow is testable.
    return (
      <div className="tn-card tn-payment__dev-skip">
        <h2>Payment (dev mode)</h2>
        <p>Stripe publishable key not set. Skipping payment for testing.</p>
        <button
          className="tn-btn tn-btn--primary"
          onClick={async () => {
            const result = await client.confirmRegistration(selectedTeam.id, registration.id);
            // Persist the SERVER's view of the registration so the
            // DonePage summary reflects status=confirmed instead of
            // the stale status=pending_payment we got back from
            // registerTeam before payment.
            setRegistration(result.registration);
            navigate(`/events/${eventId}/done`);
          }}
        >
          Mark as paid (dev)
        </button>
      </div>
    );
  }

  if (error) {
    return <div role="alert" className="tn-payment__error">{error}</div>;
  }

  if (!intent || !stripe) {
    return <div className="tn-payment__loading">Preparing checkout…</div>;
  }

  return (
    <div className="tn-payment">
      <h1 className="tn-payment__title">Payment</h1>
      <p className="tn-payment__subtitle">Registering {selectedTeam.name}</p>

      <div className="tn-payment__breakdown">
        <div className="tn-payment__line">
          <span>Registration fee</span>
          <span>{money(intent.fee_cents)}</span>
        </div>
        {intent.addon_total_cents > 0 ? (
          <div className="tn-payment__line">
            <span>Add-ons</span>
            <span>{money(intent.addon_total_cents)}</span>
          </div>
        ) : null}
        {(intent.platform_fee_cents ?? 0) > 0 ? (
          <div className="tn-payment__line">
            <span>Platform fee</span>
            <span>{money(intent.platform_fee_cents ?? 0)}</span>
          </div>
        ) : null}
        {intent.processing_fee_cents > 0 ? (
          <div className="tn-payment__line">
            <span>Processing fee</span>
            <span>{money(intent.processing_fee_cents)}</span>
          </div>
        ) : null}
        <div className="tn-payment__line tn-payment__line--total">
          <span>Total</span>
          <span>{money(intent.amount_cents)}</span>
        </div>
      </div>

      <Elements
        stripe={stripe}
        options={{
          clientSecret: intent.client_secret,
          appearance: themedStripeAppearance(),
        }}
      >
        <PaymentForm
          onSuccess={async () => {
            const result = await client.confirmRegistration(selectedTeam.id, registration.id);
            // Persist the SERVER's view (status=confirmed) into
            // FlowContext so DonePage's summary doesn't show the
            // stale status=pending_payment captured at registerTeam
            // time.
            setRegistration(result.registration);
            navigate(`/events/${eventId}/done`);
          }}
        />
      </Elements>
    </div>
  );
}
